import type { DeploymentMode } from '@shared/types/Tenant';
import type { DataMode } from './ActivationClaims';
import { logger } from '@infra/telemetry/Logger';

const KNOWN_MODES: ReadonlySet<DataMode> = new Set(['LOCAL_OFFLINE', 'REMOTE_MULTI_STATION']);

export function resolveDataMode(
  claims: { data_mode?: DataMode } | null | undefined,
): DataMode | null {
  if (!claims?.data_mode) return null;
  return claims.data_mode;
}

/** Refuse tout mode absent ou inconnu du certificat signé. */
export function resolveSignedDataMode(
  claims: { data_mode?: DataMode } | null | undefined,
): DataMode {
  const mode = resolveDataMode(claims);
  if (!mode || !KNOWN_MODES.has(mode)) {
    logger.warn({ event: 'data_mode_unknown', mode: mode ?? 'missing' });
    throw new Error('data_mode_invalid');
  }
  return mode;
}

export function isLocalOfflineMode(mode: DataMode | null): boolean {
  return mode === 'LOCAL_OFFLINE';
}

export function isRemoteMultiStationMode(mode: DataMode | null): boolean {
  return mode === 'REMOTE_MULTI_STATION';
}

export function isKnownDataMode(mode: unknown): mode is DataMode {
  return typeof mode === 'string' && KNOWN_MODES.has(mode as DataMode);
}

export function resolveDeploymentMode(claims: {
  data_mode?: DataMode;
  deployment_mode?: DeploymentMode;
} | null | undefined): DeploymentMode {
  const explicit = claims?.deployment_mode;
  if (explicit === 'hybride' || explicit === 'cloud' || explicit === 'standalone') {
    return explicit;
  }
  return claims?.data_mode === 'REMOTE_MULTI_STATION' ? 'cloud' : 'standalone';
}

export function isHybridDeployment(claims: {
  data_mode?: DataMode;
  deployment_mode?: DeploymentMode;
} | null | undefined): boolean {
  return resolveDeploymentMode(claims) === 'hybride';
}
